import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box, Card, CardContent, Typography, Table, TableBody, TableCell,
  TableHead, TableRow, Alert, TextField, MenuItem, IconButton,
  Tooltip, Grid, Tab, Tabs, CircularProgress, Autocomplete, Button,
  Paper, Divider, TableContainer, Chip, useMediaQuery, useTheme
} from '@mui/material';
import { Home, Assessment, Print, Refresh, Scoreboard, Settings, People, TableChart, Description, EmojiEvents } from '@mui/icons-material';
import axios from 'axios';
import CompanyHeader from './CompanyHeader';
import { useStages } from '../hooks/useStages';
import ExamSelect, { useTermExams, examParam } from './ExamSelect';
import { useCompany } from '../hooks/useCompany';
import { loadLogoDataUrl } from '../utils/companyLogo';

const API = process.env.REACT_APP_API_URL;

const GRADE_COLORS = {
  EE: { bg: '#e8f5e9', border: '#2e7d32', text: '#2e7d32' },
  ME: { bg: '#e3f2fd', border: '#1565c0', text: '#1565c0' },
  AE: { bg: '#fff3e0', border: '#f57c00', text: '#f57c00' },
  BE: { bg: '#ffebee', border: '#c62828', text: '#c62828' },
  '': { bg: '#f5f5f5', border: '#9e9e9e', text: '#9e9e9e' }
};

// 8-4-4 letter grades coloured by their letter family (A→green … E→red), so a
// Form 3/4 report cell isn't left grey. Keyed by the first letter (A-, B+ → A, B).
const LETTER_COLORS = {
  A: { bg: '#e8f5e9', border: '#2e7d32', text: '#2e7d32' },
  B: { bg: '#e0f2f1', border: '#00897b', text: '#00695c' },
  C: { bg: '#e3f2fd', border: '#1565c0', text: '#1565c0' },
  D: { bg: '#fff3e0', border: '#f57c00', text: '#ef6c00' },
  E: { bg: '#ffebee', border: '#c62828', text: '#c62828' },
};

const GradeCell = ({ grade, pct }) => {
  // Resolve colours by CBC band prefix (EE1→EE) first, then by 8-4-4 letter
  // family (A-, B+ → A, B), falling back to neutral grey.
  const gc = GRADE_COLORS[grade]
    || GRADE_COLORS[(grade || '').slice(0, 2)]
    || LETTER_COLORS[(grade || '')[0]]
    || GRADE_COLORS[''];
  return (
    <Box sx={{ bgcolor: gc.bg, border: `1px solid ${gc.border}`, borderRadius: 1, px: 0.5, py: 0.2, textAlign: 'center', minWidth: 60 }}>
      <Typography variant="caption" fontWeight={700} sx={{ color: gc.text, display: 'block' }}>{grade || '—'}</Typography>
      {pct !== undefined && pct !== null && (
        <Typography variant="caption" sx={{ color: gc.text, fontSize: 10 }}>{pct.toFixed(1)}%</Typography>
      )}
    </Box>
  );
};

const AssessmentReportPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { stages } = useStages();
  const company = useCompany();
  const printRef = useRef();
  const theme    = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [tab, setTab] = useState(0);

  // shared state
  const [years,   setYears]   = useState([]);
  const [scale,   setScale]   = useState([]);   // GradingScale setup table — source of truth for grades
  const [scaleBySystem, setScaleBySystem] = useState({}); // system → bands (desc), for per-subject grading
  const [classes, setClasses] = useState([]);
  const [error,   setError]   = useState('');
  const [loading, setLoading] = useState(false);

  // Tab 1 — Learner Report
  const [learnerOptions, setLearnerOptions] = useState([]);
  const [selLearner,  setSelLearner]  = useState(null);
  const [repYear,     setRepYear]     = useState('');
  const [repTerm,     setRepTerm]     = useState('');
  const [learnerReport, setLearnerReport] = useState([]);
  const [remarks,     setRemarks]     = useState('');

  // Tab 2 — Class Summary
  const [sumYear,   setSumYear]   = useState('');
  const [sumTerm,   setSumTerm]   = useState('');
  const [sumStage,  setSumStage]  = useState('');
  const [sumClass,  setSumClass]  = useState('');
  const [repExam,  setRepExam]  = useState('');   // '' = termly weighted average
  const [sumExam,  setSumExam]  = useState('');
  const repExams = useTermExams(repYear, repTerm, selLearner?.stage);
  const sumExams = useTermExams(sumYear, sumTerm, sumStage);
  const [summary,   setSummary]   = useState({ learners: [], subjects: [], matrix: {} });

  const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };

  useEffect(() => {
    const loadMeta = async () => {
      try {
        const [yRes, lRes, cpRes] = await Promise.all([
          axios.get(`${API}/api/academics/years`, { headers }),
          axios.get(`${API}/api/academics/learners`, { headers }),
          axios.get(`${API}/api/academics/current-period`, { headers }).catch(() => ({ data: {} })),
        ]);
        setYears(yRes.data);
        setLearnerOptions(lRes.data);

        // Default both tabs' Year + Term to the current academic period (active
        // year + the term in session) so reports open ready to run.
        const cpYear = cpRes.data?.year || (yRes.data || []).find(y => y.isActive)?.year || '';
        // Term options use numeric values ({1,2,3}), so keep the term a number
        // (not a string) or the Select won't match and stays blank.
        const cpTerm = cpRes.data?.term || '';
        if (cpYear) { setRepYear(cpYear); setSumYear(cpYear); }
        if (cpTerm) { setRepTerm(cpTerm); setSumTerm(cpTerm); }

        // Deep-link from the Learners page: preselect the learner + active year
        // and open the Learner Report tab so their results show immediately.
        const admNo = location.state?.learnerAdmNo;
        if (admNo) {
          const match = (lRes.data || []).find(l => l.admNo === admNo);
          if (match) {
            setSelLearner(match);
            const active = (yRes.data || []).find(y => y.isActive);
            if (active) setRepYear(active.year);
            setTab(0);
          }
        }
      } catch { /* silent */ }
    };
    loadMeta();
  }, []); // eslint-disable-line

  // The grade scale depends on the CURRICULUM of the stage in view: the class
  // summary uses its selected stage; the learner report uses the learner's stage.
  // So a Form 3/4 report grades on 8-4-4 letters while Grade 10 uses CBC levels.
  const curriculumForStage = (code) => stages.find(s => s.code === code)?.curriculum || 'CBC';
  const activeStageCode = tab === 1 ? sumStage : (selLearner?.stage || '');
  const activeSystem = curriculumForStage(activeStageCode);
  useEffect(() => {
    axios.get(`${API}/api/academics/grading-scale?system=${encodeURIComponent(activeSystem)}`, { headers })
      .then(r => setScale(r.data || []))
      .catch(() => {});
  }, [activeSystem]); // eslint-disable-line

  // All grading bands grouped by system → lets the class summary grade each
  // subject cell on the subject's own assigned grading system.
  useEffect(() => {
    axios.get(`${API}/api/academics/grading-scale`, { headers })
      .then(r => {
        const map = {};
        (r.data || []).forEach(b => { (map[b.system] = map[b.system] || []).push(b); });
        Object.values(map).forEach(arr => arr.sort((a, b) => b.minPercent - a.minPercent));
        setScaleBySystem(map);
      })
      .catch(() => {});
  }, []); // eslint-disable-line

  useEffect(() => {
    if (!sumStage) { setClasses([]); setSumClass(''); return; }
    const role = (JSON.parse(localStorage.getItem('user') || '{}')).role;
    const load = async () => {
      try {
        // Class teachers only see their own class(es) here — not other streams.
        const url = role === 'classTeacher'
          ? `${API}/api/academics/class-teacher/classes`
          : `${API}/api/academics/classes?stageCode=${sumStage}`;
        const { data } = await axios.get(url, { headers });
        setClasses((data || []).filter(c => c.stageCode === sumStage));
      } catch { setClasses([]); }
    };
    load();
  }, [sumStage]); // eslint-disable-line

  // Class teachers: prefill Stage + Class + Year from their assigned class so the
  // Class Summary and Learner Report are scoped to their class out of the box.
  useEffect(() => {
    const role = (JSON.parse(localStorage.getItem('user') || '{}')).role;
    if (role !== 'classTeacher') return;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/api/academics/class-teacher/classes`, { headers });
        if (data?.length) {
          const cls = data[0];
          setSumStage(cls.stageCode);
          // Only prefill the Class when the class actually has a stream. A
          // streamless stage IS the whole grade — there's no sub-class to pick,
          // so leave it on "All classes" instead of selecting a phantom class.
          setSumClass(cls.streamCode ? cls.classCode : '');
        }
      } catch { /* non-fatal */ }
    })();
  }, []); // eslint-disable-line

  // Tab 1 load
  const loadLearnerReport = useCallback(async () => {
    if (!selLearner || !repYear || !repTerm) { setLearnerReport([]); return; }
    setLoading(true); setError('');
    try {
      const { data } = await axios.get(
        `${API}/api/academics/assessment-scores/term-report?admNo=${selLearner.admNo}&academicYear=${repYear}&term=${repTerm}${examParam(repExams, repExam) ? '&exam=' + examParam(repExams, repExam) : ''}`,
        { headers }
      );
      setLearnerReport(data.scores || []);
    } catch (err) { setError(err.response?.data?.error || 'Failed to load report'); }
    finally { setLoading(false); }
  }, [selLearner, repYear, repTerm, repExam, repExams]); // eslint-disable-line

  useEffect(() => { loadLearnerReport(); }, [loadLearnerReport]);

  // Tab 2 load
  const loadClassSummary = useCallback(async () => {
    if (!sumYear || !sumTerm || !sumStage) { setSummary({ learners: [], subjects: [], matrix: {} }); return; }
    setLoading(true); setError('');
    try {
      const params = new URLSearchParams({ academicYear: sumYear, term: sumTerm, stageCode: sumStage });
      if (sumClass) params.set('classCode', sumClass);
      if (examParam(sumExams, sumExam)) params.set('exam', examParam(sumExams, sumExam));
      const [scoresRes, stageSubjRes] = await Promise.all([
        axios.get(`${API}/api/academics/assessment-scores?${params}`, { headers }),
        axios.get(`${API}/api/academics/stage-learning-areas?year=${sumYear}&stageCode=${sumStage}`, { headers }),
      ]);
      const data = scoresRes.data;

      // Column order follows the Stage Subjects "Seq" for this stage/year.
      // Subjects with no configured sequence fall to the end, then by name.
      // Also capture each subject's assigned grading system so cells grade on it.
      const seqMap = {};
      const subjectSystem = {};
      (stageSubjRes.data || []).forEach(s => {
        seqMap[s.areaCode] = s.sequence ?? 999;
        if (s.gradingSystem) subjectSystem[s.areaCode] = s.gradingSystem;
      });

      // Build matrix: learnerAdmNo → { subjectCode: { pct, grade } }
      const subjectSet = {};
      const learnerSet = {};
      data.forEach(d => {
        subjectSet[d.learningAreaCode] = d.learningAreaName || d.learningAreaCode;
        if (!learnerSet[d.learnerAdmNo]) learnerSet[d.learnerAdmNo] = { admNo: d.learnerAdmNo, name: d.learnerName };
      });
      // Rank subject columns from the best-performing subject (highest class
      // mean) to the weakest. Seq / name break ties, so subjects with no scores
      // still fall in a stable order at the end.
      const subjMean = {};
      Object.keys(subjectSet).forEach(code => {
        const vals = data.filter(d => d.learningAreaCode === code)
          .map(d => d.percentage).filter(p => p !== undefined && p !== null);
        subjMean[code] = vals.length ? vals.reduce((a, b) => a + b, 0) / vals.length : null;
      });
      const subjects = Object.entries(subjectSet)
        .map(([code, name]) => ({ code, name, seq: seqMap[code] ?? 999, classMean: subjMean[code] }))
        .sort((a, b) =>
          (b.classMean ?? -1) - (a.classMean ?? -1) ||
          a.seq - b.seq ||
          a.name.localeCompare(b.name));
      const learnerList = Object.values(learnerSet).sort((a, b) => a.name.localeCompare(b.name));
      const matrix = {};
      data.forEach(d => {
        if (!matrix[d.learnerAdmNo]) matrix[d.learnerAdmNo] = {};
        matrix[d.learnerAdmNo][d.learningAreaCode] = { pct: d.percentage, grade: d.grade };
      });
      setSummary({ learners: learnerList, subjects, matrix, subjectSystem });
    } catch (err) { setError(err.response?.data?.error || 'Failed to load summary'); }
    finally { setLoading(false); }
  }, [sumYear, sumTerm, sumStage, sumClass, sumExam, sumExams]); // eslint-disable-line

  useEffect(() => { loadClassSummary(); }, [loadClassSummary]);

  // Resolve a grade code from a percentage using the GradingScale setup table —
  // mirrors the backend (whole-percent bands) so the mean grade agrees with per-subject grades.
  const gradeFor = useCallback((pct) => {
    if (pct === null || pct === undefined) return '';
    const p = Math.round(pct);
    return scale.find(g => p >= g.minPercent && p <= g.maxPercent)?.code || '';
  }, [scale]);

  // Grade a class-summary cell on the SUBJECT's assigned grading system (falls
  // back to the stage scale when the subject has none). Keeps each subject's
  // grade consistent with what Score Entry / report cards store.
  const gradeForArea = useCallback((areaCode, pct) => {
    if (pct === null || pct === undefined) return '';
    const sys = summary.subjectSystem?.[areaCode];
    const bands = (sys && scaleBySystem[sys]?.length) ? scaleBySystem[sys] : scale;
    const p = Math.round(pct);
    return bands.find(g => p >= g.minPercent && p <= g.maxPercent)?.code || '';
  }, [summary, scaleBySystem, scale]);

  // Grade a learner-report row on the SUBJECT's assigned grading system (carried
  // on the row as `gradingSystem` from the backend), falling back to the stage
  // scale. Keeps the report's per-subject grade in step with Score Entry.
  const gradeForRow = useCallback((r) => {
    if (r?.percentage === null || r?.percentage === undefined) return '';
    const bands = (r.gradingSystem && scaleBySystem[r.gradingSystem]?.length)
      ? scaleBySystem[r.gradingSystem] : scale;
    const p = Math.round(r.percentage);
    return bands.find(g => p >= g.minPercent && p <= g.maxPercent)?.code || '';
  }, [scaleBySystem, scale]);

  const overallPct = learnerReport.length
    ? learnerReport.reduce((s, r) => s + (r.percentage || 0), 0) / learnerReport.length
    : null;

  const overallGrade = gradeFor(overallPct);

  const totalRawSum  = learnerReport.reduce((s, r) => s + (r.totalRaw || 0), 0);
  const totalMaxSum  = learnerReport.reduce((s, r) => s + (r.totalMax || 0), 0);

  // Class summary — ranked learners with mean % and grade
  const rankedLearners = useMemo(() => {
    return [...summary.learners]
      .map(l => {
        const cells = summary.subjects.map(s => summary.matrix[l.admNo]?.[s.code]?.pct).filter(p => p !== undefined && p !== null);
        const meanPct = cells.length ? cells.reduce((a, b) => a + b, 0) / cells.length : null;
        const meanGrade = gradeFor(meanPct);
        return { ...l, meanPct, meanGrade };
      })
      .sort((a, b) => (b.meanPct ?? -1) - (a.meanPct ?? -1))
      .map((l, i) => ({ ...l, rank: l.meanPct !== null ? i + 1 : '—' }));
  }, [summary, gradeFor]);

  const handleLearnerPrint = () => {
    document.body.removeAttribute('data-print-target');
    window.print();
  };

  const handleClassPrint = () => {
    document.body.setAttribute('data-print-target', 'class');
    window.print();
    document.body.removeAttribute('data-print-target');
  };

  const handleClassExcel = () => {
    const hdrs = ['Rank', 'Name', ...summary.subjects.map(s => s.code), 'Mean %', 'Grade'];
    const rows = rankedLearners.map(l => [
      l.rank,
      l.name,
      ...summary.subjects.map(s => {
        const cell = summary.matrix[l.admNo]?.[s.code];
        return cell ? `${cell.pct?.toFixed(1)}% (${gradeForArea(s.code, cell.pct)})` : '—';
      }),
      l.meanPct !== null ? `${l.meanPct.toFixed(1)}%` : '—',
      l.meanGrade || '—',
    ]);
    const avgRow = [
      '—', 'Class Average',
      ...summary.subjects.map(s => {
        const vals = summary.learners.map(l => summary.matrix[l.admNo]?.[s.code]?.pct).filter(p => p !== undefined);
        const avg = vals.length ? vals.reduce((a, b) => a + b, 0) / vals.length : null;
        return avg !== null ? `${avg.toFixed(1)}%` : '—';
      }),
      '', '',
    ];
    const csv = [hdrs, ...rows, avgRow]
      .map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(','))
      .join('\r\n');
    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8;' }));
    a.download = `class-summary-${sumStage}-${sumYear}-term${sumTerm}.csv`;
    a.click();
  };

  const handleClassWord = async () => {
    const subjectHeaders = summary.subjects.map(s => `<th style="border:1px solid #999;padding:4px;background:#f0f0f0" title="${s.name}">${s.code}</th>`).join('');
    const bodyRows = rankedLearners.map(l => {
      const cells = summary.subjects.map(s => {
        const cell = summary.matrix[l.admNo]?.[s.code];
        return `<td style="border:1px solid #999;padding:4px;text-align:center">${cell ? `${cell.pct?.toFixed(1)}% (${gradeForArea(s.code, cell.pct)})` : '—'}</td>`;
      }).join('');
      return `<tr><td style="border:1px solid #999;padding:4px;text-align:center"><b>${l.rank}</b></td><td style="border:1px solid #999;padding:4px">${l.name}</td>${cells}<td style="border:1px solid #999;padding:4px;text-align:center"><b>${l.meanPct !== null ? `${l.meanPct.toFixed(1)}%` : '—'}</b></td><td style="border:1px solid #999;padding:4px;text-align:center"><b>${l.meanGrade || '—'}</b></td></tr>`;
    }).join('');

    const logoSrc = await loadLogoDataUrl(company);
    const schoolHeader = company ? `
      <table style="width:100%;border:none;margin-bottom:12px">
        <tr>
          <td style="text-align:center;border:none;padding:0">
            ${logoSrc ? `<img src="${logoSrc}" style="height:50px;margin-bottom:4px"><br>` : ''}
            <strong style="font-size:16px;color:${company.brandColor || '#0a1931'}">${company.name}</strong><br>
            ${company.motto ? `<em style="font-size:11px;color:#666">${company.motto}</em><br>` : ''}
            ${company.address || company.city ? `<span style="font-size:10px">${[company.address, company.city, company.county].filter(Boolean).join(', ')}${company.postalCode ? ` · P.O. ${company.postalCode}` : ''}</span><br>` : ''}
            ${company.phone || company.email ? `<span style="font-size:10px;color:#666">${[company.phone, company.phone2, company.email, company.website].filter(Boolean).join(' · ')}</span><br>` : ''}
            ${company.kraPin ? `<span style="font-size:10px;color:#666">Revenue Authority No.: ${company.kraPin}</span><br>` : ''}
          </td>
        </tr>
      </table>
      <hr style="border:none;border-top:2px solid #ccc;margin:8px 0">
    ` : '';

    const html = `<html xmlns:o='urn:schemas-microsoft-com:office:office' xmlns:w='urn:schemas-microsoft-com:office:word'><head><meta charset='utf-8'></head><body>${schoolHeader}<h2 style="text-align:center">CLASS SUMMARY REPORT</h2><p style="text-align:center">${sumStage} · Year ${sumYear} · Term ${sumTerm}${sumClass ? ` · ${sumClass}` : ''}</p><table border="0" cellpadding="4" cellspacing="0" width="100%" style="border-collapse:collapse"><thead><tr><th style="border:1px solid #999;padding:4px;background:#f0f0f0">Rank</th><th style="border:1px solid #999;padding:4px;background:#f0f0f0">Learner</th>${subjectHeaders}<th style="border:1px solid #999;padding:4px;background:#f0f0f0">Mean %</th><th style="border:1px solid #999;padding:4px;background:#f0f0f0">Grade</th></tr></thead><tbody>${bodyRows}</tbody></table></body></html>`;
    const blob = new Blob(['﻿', html], { type: 'application/msword' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `class-summary-${sumStage}-${sumYear}-term${sumTerm}.doc`;
    a.click();
  };

  return (
    <>
      {/* ── Print styles ── */}
      <style>{`
        @page { size: A4 portrait; margin: 0.5in; }
        @media print {
          body * { visibility: hidden; }
          body:not([data-print-target="class"]) #assessment-print-area,
          body:not([data-print-target="class"]) #assessment-print-area * { visibility: visible; }
          body:not([data-print-target="class"]) #assessment-print-area { position: absolute; left: 0; top: 0; width: 100%; padding: 32px; box-sizing: border-box; }
          body[data-print-target="class"] #class-summary-print-area,
          body[data-print-target="class"] #class-summary-print-area * { visibility: visible; }
          body[data-print-target="class"] #class-summary-print-area { position: absolute; left: 0; top: 0; width: 100%; padding: 24px; box-sizing: border-box; }
          body[data-print-target="class"] #class-summary-print-area table { font-size: 10px; border-collapse: collapse; width: 100%; }
          body[data-print-target="class"] #class-summary-print-area th,
          body[data-print-target="class"] #class-summary-print-area td { border: 1px solid #999; padding: 3px 5px; }
        }
        @media screen {
          #assessment-print-area, #class-summary-print-area { display: none; }
        }
      `}</style>

      {/* ── Print area (hidden on screen, shown on print) ── */}
      <Box id="assessment-print-area" ref={printRef} sx={{ p: 4, fontFamily: 'serif' }}>
        <CompanyHeader title="Learner Term Report" subtitle={`${repYear} · Term ${repTerm}`} />
        {selLearner && (
          <Box mb={2} sx={{ border: '1px solid #ccc', p: 2, borderRadius: 1 }}>
            <Grid container spacing={1}>
              <Grid item xs={6}><Typography variant="body2"><strong>Name:</strong> {selLearner.name}</Typography></Grid>
              <Grid item xs={6}><Typography variant="body2"><strong>Adm No:</strong> {selLearner.admNo}</Typography></Grid>
              <Grid item xs={6}><Typography variant="body2"><strong>Stage:</strong> {selLearner.stage}</Typography></Grid>
              <Grid item xs={6}><Typography variant="body2"><strong>Class:</strong> {selLearner.classCode || '—'}</Typography></Grid>
            </Grid>
          </Box>
        )}
        {learnerReport.length > 0 && (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
            <thead>
              <tr style={{ backgroundColor: '#f0f0f0' }}>
                <th style={{ border: '1px solid #ccc', padding: '4px 8px', textAlign: 'left' }}>Learning Area</th>
                <th style={{ border: '1px solid #ccc', padding: '4px 8px', textAlign: 'center' }}>Score</th>
                <th style={{ border: '1px solid #ccc', padding: '4px 8px', textAlign: 'center' }}>Max</th>
                <th style={{ border: '1px solid #ccc', padding: '4px 8px', textAlign: 'center' }}>%</th>
                <th style={{ border: '1px solid #ccc', padding: '4px 8px', textAlign: 'center' }}>Grade</th>
              </tr>
            </thead>
            <tbody>
              {learnerReport.map(r => (
                <tr key={r.learningAreaCode}>
                  <td style={{ border: '1px solid #ccc', padding: '4px 8px' }}>{r.learningAreaName || r.learningAreaCode}</td>
                  <td style={{ border: '1px solid #ccc', padding: '4px 8px', textAlign: 'center' }}>{r.totalRaw ?? '—'}</td>
                  <td style={{ border: '1px solid #ccc', padding: '4px 8px', textAlign: 'center' }}>{r.totalMax}</td>
                  <td style={{ border: '1px solid #ccc', padding: '4px 8px', textAlign: 'center' }}>{r.percentage?.toFixed(1)}%</td>
                  <td style={{ border: '1px solid #ccc', padding: '4px 8px', textAlign: 'center', fontWeight: 'bold' }}>{gradeForRow(r) || '—'}</td>
                </tr>
              ))}
              <tr style={{ fontWeight: 'bold', backgroundColor: '#f9f9f9' }}>
                <td style={{ border: '1px solid #ccc', padding: '4px 8px' }}>TOTAL / MEAN</td>
                <td style={{ border: '1px solid #ccc', padding: '4px 8px', textAlign: 'center' }}>{totalRawSum}</td>
                <td style={{ border: '1px solid #ccc', padding: '4px 8px', textAlign: 'center' }}>{totalMaxSum}</td>
                <td style={{ border: '1px solid #ccc', padding: '4px 8px', textAlign: 'center' }}>{overallPct?.toFixed(1)}%</td>
                <td style={{ border: '1px solid #ccc', padding: '4px 8px', textAlign: 'center', fontWeight: 'bold' }}>{overallGrade || '—'}</td>
              </tr>
            </tbody>
          </table>
        )}
        <Box mt={3}>
          <Typography variant="body2" fontWeight={700}>Class Teacher Remarks:</Typography>
          <Box sx={{ border: '1px solid #ccc', borderRadius: 1, p: 1, minHeight: 60, mt: 0.5 }}>
            <Typography variant="body2">{remarks || '_____________________________________'}</Typography>
          </Box>
        </Box>
        <Box mt={2} display="flex" justifyContent="space-between">
          <Typography variant="caption">Class Teacher: _________________________</Typography>
          <Typography variant="caption">Principal: _________________________</Typography>
          <Typography variant="caption">Date: _________________________</Typography>
        </Box>
      </Box>

      {/* ── Class Summary Print Area ── */}
      <Box id="class-summary-print-area">
        <CompanyHeader title="CLASS SUMMARY REPORT" subtitle={`${sumStage} · Year ${sumYear} · Term ${sumTerm}${sumClass ? ` · ${sumClass}` : ''}`} />
        <Box textAlign="center" mb={2}>
          <Typography variant="body2" color="text.secondary">{sumStage} · Academic Year {sumYear} · Term {sumTerm}{sumClass ? ` · ${sumClass}` : ''}</Typography>
        </Box>
        {rankedLearners.length > 0 && (
          <table>
            <thead>
              <tr style={{ backgroundColor: '#f0f0f0' }}>
                <th style={{ textAlign: 'center' }}>Rank</th>
                <th style={{ textAlign: 'left' }}>Learner</th>
                {summary.subjects.map(s => <th key={s.code} style={{ textAlign: 'center' }} title={s.name}>{s.code}</th>)}
                <th style={{ textAlign: 'center' }}>Mean %</th>
                <th style={{ textAlign: 'center' }}>Grade</th>
              </tr>
            </thead>
            <tbody>
              {rankedLearners.map(l => (
                <tr key={l.admNo}>
                  <td style={{ textAlign: 'center', fontWeight: 'bold' }}>{l.rank}</td>
                  <td>{l.name}</td>
                  {summary.subjects.map(s => {
                    const cell = summary.matrix[l.admNo]?.[s.code];
                    return <td key={s.code} style={{ textAlign: 'center' }}>{cell ? `${cell.pct?.toFixed(1)}% (${gradeForArea(s.code, cell.pct)})` : '—'}</td>;
                  })}
                  <td style={{ textAlign: 'center', fontWeight: 'bold' }}>{l.meanPct !== null ? `${l.meanPct.toFixed(1)}%` : '—'}</td>
                  <td style={{ textAlign: 'center', fontWeight: 'bold' }}>{l.meanGrade || '—'}</td>
                </tr>
              ))}
              <tr style={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>
                <td />
                <td>Class Average</td>
                {summary.subjects.map(s => {
                  const vals = summary.learners.map(l => summary.matrix[l.admNo]?.[s.code]?.pct).filter(p => p !== undefined);
                  const avg = vals.length ? vals.reduce((a, b) => a + b, 0) / vals.length : null;
                  return <td key={s.code} style={{ textAlign: 'center' }}>{avg !== null ? `${avg.toFixed(1)}%` : '—'}</td>;
                })}
                <td />
                <td />
              </tr>
            </tbody>
          </table>
        )}
        <Box mt={3} display="flex" justifyContent="space-between">
          <Typography variant="caption">Class Teacher: _________________________</Typography>
          <Typography variant="caption">Principal: _________________________</Typography>
          <Typography variant="caption">Date: _________________________</Typography>
        </Box>
      </Box>

      {/* ── Screen UI ── */}
      <Box sx={{ maxWidth: 1200, mx: 'auto', mt: 4, mb: 4 }}>
        <Card elevation={3} sx={{ position: { xs: 'sticky', sm: 'relative' }, top: { xs: 0, sm: 'auto' }, zIndex: 10, mb: { xs: 0, sm: 3 } }}>
          <CardContent sx={{ p: { xs: 1, sm: 2 } }}>
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={2} flexWrap="wrap" gap={1}>
              <Box display="flex" alignItems="center" gap={1}>
                <Assessment sx={{ color: '#004d40', fontSize: { xs: 20, sm: 24 } }} />
                <Typography variant="h5" sx={{ color: '#004d40', fontWeight: 700, fontSize: { xs: '1.15rem', sm: '1.5rem' } }}>Assessment Reports</Typography>
              </Box>
              <Box display="flex" gap={0.5} flexWrap="wrap" justifyContent="flex-end">
                <Tooltip title="Assessment Setup"><IconButton onClick={() => navigate('/academics/assessment-setup')} size="small" sx={{ bgcolor: '#e0f7fa' }}><Settings sx={{ color: '#00838f', fontSize: { xs: 18, sm: 24 } }} /></IconButton></Tooltip>
                <Tooltip title="Score Entry"><IconButton onClick={() => navigate('/academics/score-entry')} size="small" sx={{ bgcolor: '#e8eaf6' }}><Scoreboard sx={{ color: '#283593', fontSize: { xs: 18, sm: 24 } }} /></IconButton></Tooltip>
                <Tooltip title="Merit List"><IconButton onClick={() => navigate('/academics/merit-list')} size="small" sx={{ bgcolor: '#fff8e1' }}><EmojiEvents sx={{ color: '#f9a825', fontSize: { xs: 18, sm: 24 } }} /></IconButton></Tooltip>
                <Tooltip title="Learners List"><IconButton onClick={() => navigate('/learners')} size="small" sx={{ bgcolor: '#fbe9e7' }}><People sx={{ color: '#e65100', fontSize: { xs: 18, sm: 24 } }} /></IconButton></Tooltip>
                <Tooltip title="Back to Academics"><IconButton onClick={() => navigate('/academics')} size="small" sx={{ bgcolor: 'background.paper' }}><Home sx={{ fontSize: { xs: 18, sm: 24 } }} /></IconButton></Tooltip>
              </Box>
            </Box>

            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

            <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="scrollable" scrollButtons="auto" sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}>
              <Tab label="Learner Report" />
              <Tab label="Class Summary" />
              <Tab label="Print / Report Card" />
            </Tabs>

            {loading && <Box display="flex" justifyContent="center" my={4}><CircularProgress /></Box>}

            {/* ── Tab 1: Learner Report ── */}
            {tab === 0 && !loading && (
              <Box>
                <Box display="flex" gap={{ xs: 1, sm: 2 }} mb={3} flexWrap="wrap" alignItems="flex-start" sx={{ px: { xs: 1, sm: 0 } }}>
                  <Autocomplete
                    options={learnerOptions}
                    getOptionLabel={o => `${o.admNo} — ${o.name}`}
                    onChange={(_, v) => setSelLearner(v)}
                    sx={{ minWidth: { xs: 'calc(100% - 40px)', sm: 280 }, flexGrow: { xs: 1, sm: 0 } }}
                    renderInput={params => <TextField {...params} label="Search Learner" size="small" placeholder="Type to filter…" />}
                  />
                  <TextField select label="Year" size="small" value={repYear} onChange={e => setRepYear(e.target.value)} sx={{ minWidth: { xs: 'calc(50% - 8px)', sm: 140 } }}>
                    <MenuItem value=""><em>— Select —</em></MenuItem>
                    {years.map(y => <MenuItem key={y.year} value={y.year}>{y.year}</MenuItem>)}
                  </TextField>
                  <TextField select label="Term" size="small" value={repTerm} onChange={e => setRepTerm(e.target.value)} sx={{ minWidth: { xs: 'calc(50% - 8px)', sm: 120 } }}>
                    <MenuItem value=""><em>— Select —</em></MenuItem>
                    {[1,2,3].map(t => <MenuItem key={t} value={t}>Term {t}</MenuItem>)}
                  </TextField>
                  <ExamSelect exams={repExams} value={repExam} onChange={setRepExam} />
                  <Tooltip title="Refresh"><IconButton onClick={loadLearnerReport} size="small"><Refresh sx={{ fontSize: { xs: 18, sm: 24 } }} /></IconButton></Tooltip>
                  {learnerReport.length > 0 && (
                    <Button startIcon={<Print />} variant="outlined" size="small" onClick={() => setTab(2)} sx={{ display: { xs: 'none', sm: 'inline-flex' } }}>Print View</Button>
                  )}
                </Box>

                {selLearner && learnerReport.length > 0 ? (
                  <>
                    <Paper variant="outlined" sx={{ p: { xs: 1, sm: 1.5 }, mb: 2, bgcolor: '#f5f5f5', mx: { xs: 1, sm: 0 } }}>
                      <Grid container spacing={1}>
                        <Grid item xs={12} sm={3}><Typography variant="body2"><strong>Name:</strong> {selLearner.name}</Typography></Grid>
                        <Grid item xs={12} sm={3}><Typography variant="body2"><strong>Adm No:</strong> {selLearner.admNo}</Typography></Grid>
                        <Grid item xs={12} sm={3}><Typography variant="body2"><strong>Stage:</strong> {selLearner.stage}</Typography></Grid>
                        <Grid item xs={12} sm={3}><Typography variant="body2"><strong>Class:</strong> {selLearner.classCode || '—'}</Typography></Grid>
                      </Grid>
                    </Paper>
                    <Box sx={{ overflowX: 'auto', mx: { xs: 1, sm: 0 } }}>
                      <TableContainer>
                        <Table size="small">
                          <TableHead>
                            <TableRow sx={{ bgcolor: 'grey.50' }}>
                              <TableCell><strong>Learning Area</strong></TableCell>
                              <TableCell align="center"><strong>Raw Score</strong></TableCell>
                              <TableCell align="center"><strong>Out Of</strong></TableCell>
                              <TableCell align="center"><strong>%</strong></TableCell>
                              <TableCell align="center"><strong>Grade</strong></TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {learnerReport.map(r => (
                              <TableRow key={r.learningAreaCode} hover>
                                <TableCell><strong>{r.learningAreaName || r.learningAreaCode}</strong></TableCell>
                                <TableCell align="center">{r.totalRaw ?? '—'}</TableCell>
                                <TableCell align="center">{r.totalMax}</TableCell>
                                <TableCell align="center">{r.percentage?.toFixed(1)}%</TableCell>
                                <TableCell align="center">
                                  <GradeCell grade={gradeForRow(r)} />
                                </TableCell>
                              </TableRow>
                            ))}
                            <TableRow sx={{ bgcolor: 'grey.100' }}>
                              <TableCell><strong>TOTAL / MEAN</strong></TableCell>
                              <TableCell align="center"><strong>{totalRawSum}</strong></TableCell>
                              <TableCell align="center"><strong>{totalMaxSum}</strong></TableCell>
                              <TableCell align="center"><strong>{overallPct?.toFixed(1)}%</strong></TableCell>
                              <TableCell align="center"><GradeCell grade={overallGrade} /></TableCell>
                            </TableRow>
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Box>
                  </>
                ) : selLearner && repYear && repTerm ? (
                  <Alert severity="info">No assessment scores found for this learner in Term {repTerm}, {repYear}.</Alert>
                ) : (
                  <Typography variant="body2" color="text.secondary">Select a learner, year, and term to view the report.</Typography>
                )}
              </Box>
            )}

            {/* ── Tab 2: Class Summary ── */}
            {tab === 1 && !loading && (
              <Box>
                <Box display="flex" gap={{ xs: 1, sm: 2 }} mb={2} flexWrap="wrap" alignItems="flex-start" sx={{ px: { xs: 1, sm: 0 } }}>
                  <TextField select label="Year" size="small" value={sumYear} onChange={e => setSumYear(e.target.value)} sx={{ minWidth: { xs: 'calc(50% - 8px)', sm: 140 } }}>
                    <MenuItem value=""><em>— Select —</em></MenuItem>
                    {years.map(y => <MenuItem key={y.year} value={y.year}>{y.year}</MenuItem>)}
                  </TextField>
                  <TextField select label="Term" size="small" value={sumTerm} onChange={e => setSumTerm(e.target.value)} sx={{ minWidth: { xs: 'calc(50% - 8px)', sm: 120 } }}>
                    <MenuItem value=""><em>— Select —</em></MenuItem>
                    {[1,2,3].map(t => <MenuItem key={t} value={t}>Term {t}</MenuItem>)}
                  </TextField>
                  <ExamSelect exams={sumExams} value={sumExam} onChange={setSumExam} />
                  <TextField select label="Stage" size="small" value={sumStage} onChange={e => { setSumStage(e.target.value); setSumClass(''); }} sx={{ minWidth: { xs: 'calc(50% - 8px)', sm: 150 } }}>
                    <MenuItem value=""><em>— Select —</em></MenuItem>
                    {stages.map(s => <MenuItem key={s.code} value={s.code}>{s.code} — {s.name}</MenuItem>)}
                  </TextField>
                  <TextField select label="Class" size="small" value={sumClass} onChange={e => setSumClass(e.target.value)} sx={{ minWidth: { xs: 'calc(50% - 8px)', sm: 160 } }}>
                    <MenuItem value=""><em>All classes</em></MenuItem>
                    {classes.map(c => <MenuItem key={c.classCode} value={c.classCode}>{c.className}</MenuItem>)}
                  </TextField>
                  <Tooltip title="Refresh"><IconButton onClick={loadClassSummary} size="small"><Refresh sx={{ fontSize: { xs: 18, sm: 24 } }} /></IconButton></Tooltip>
                  {rankedLearners.length > 0 && (
                    <>
                      <Tooltip title="Print / Save as PDF">
                        <Button size="small" variant="outlined" startIcon={<Print />} onClick={handleClassPrint} sx={{ display: { xs: 'none', sm: 'inline-flex' } }}>Print / PDF</Button>
                      </Tooltip>
                      <Tooltip title="Export to Excel (CSV)">
                        <Button size="small" variant="outlined" startIcon={<TableChart />} onClick={handleClassExcel} sx={{ display: { xs: 'none', sm: 'inline-flex' } }}>Excel</Button>
                      </Tooltip>
                      <Tooltip title="Export to Word">
                        <Button size="small" variant="outlined" startIcon={<Description />} onClick={handleClassWord} sx={{ display: { xs: 'none', sm: 'inline-flex' } }}>Word</Button>
                      </Tooltip>
                    </>
                  )}
                </Box>

                {rankedLearners.length > 0 && isMobile ? (
                  /* Mobile: one card per learner (the wide matrix won't fit) */
                  <Box display="flex" flexDirection="column" gap={1.2}>
                    {rankedLearners.map(l => (
                      <Card key={l.admNo} variant="outlined" sx={{ borderRadius: 2 }}>
                        <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                          <Box display="flex" alignItems="center" gap={1} mb={1}>
                            <Chip label={`#${l.rank}`} size="small" color="primary" variant="outlined" />
                            <Typography variant="body2" fontWeight={700} sx={{ flex: 1, minWidth: 0 }}>{l.name}</Typography>
                            <Typography variant="caption" fontWeight={700} sx={{ mr: 0.5 }}>
                              {l.meanPct !== null ? `${l.meanPct.toFixed(1)}%` : '—'}
                            </Typography>
                            <GradeCell grade={l.meanGrade} />
                          </Box>
                          <Box display="flex" gap={0.5} flexWrap="wrap">
                            {summary.subjects.map(s => {
                              const cell = summary.matrix[l.admNo]?.[s.code];
                              return (
                                <Box key={s.code} display="flex" alignItems="center" gap={0.5}
                                  sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, px: 0.75, py: 0.25 }}>
                                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>{s.code}</Typography>
                                  {cell
                                    ? <GradeCell grade={gradeForArea(s.code, cell.pct)} pct={cell.pct} />
                                    : <Typography variant="caption" color="text.disabled">—</Typography>}
                                </Box>
                              );
                            })}
                          </Box>
                        </CardContent>
                      </Card>
                    ))}
                  </Box>
                ) : rankedLearners.length > 0 ? (
                  <Box sx={{ overflowX: 'auto', mx: { xs: 1, sm: 0 } }}>
                    <TableContainer sx={{ overflowX: 'auto' }}>
                      <Table size="small" stickyHeader>
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ minWidth: 40, bgcolor: 'grey.100', fontWeight: 700, textAlign: 'center', fontSize: { xs: 11, sm: 12 } }}>#</TableCell>
                            <TableCell sx={{ minWidth: 120, bgcolor: 'grey.100', fontWeight: 700, fontSize: { xs: 11, sm: 12 } }}>Learner</TableCell>
                            {summary.subjects.map(s => (
                              <Tooltip key={s.code} title={s.name} arrow>
                                <TableCell align="center" sx={{ minWidth: 48, bgcolor: 'grey.100', fontWeight: 700, fontSize: { xs: 9, sm: 11 } }}>
                                  {s.code}
                                </TableCell>
                              </Tooltip>
                            ))}
                            <TableCell align="center" sx={{ minWidth: 60, bgcolor: '#e8f5e9', fontWeight: 700, fontSize: { xs: 9, sm: 11 } }}>Mean %</TableCell>
                            <TableCell align="center" sx={{ minWidth: 60, bgcolor: '#e8f5e9', fontWeight: 700, fontSize: { xs: 9, sm: 11 } }}>Grade</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {rankedLearners.map(l => (
                            <TableRow key={l.admNo} hover>
                              <TableCell align="center" sx={{ fontWeight: 700, color: 'text.secondary', fontSize: { xs: 10, sm: 12 } }}>{l.rank}</TableCell>
                              <TableCell sx={{ fontSize: { xs: 10, sm: 12 } }}>{l.name}</TableCell>
                              {summary.subjects.map(s => {
                                const cell = summary.matrix[l.admNo]?.[s.code];
                                return (
                                  <TableCell key={s.code} align="center" sx={{ p: 0.5 }}>
                                    {cell ? <GradeCell grade={gradeForArea(s.code, cell.pct)} pct={cell.pct} /> : <Typography variant="caption" color="text.disabled">—</Typography>}
                                  </TableCell>
                                );
                              })}
                              <TableCell align="center" sx={{ bgcolor: '#f1f8e9' }}>
                                <Typography variant="caption" fontWeight={700}>{l.meanPct !== null ? `${l.meanPct.toFixed(1)}%` : '—'}</Typography>
                              </TableCell>
                              <TableCell align="center" sx={{ p: 0.5, bgcolor: '#f1f8e9' }}>
                                <GradeCell grade={l.meanGrade} />
                              </TableCell>
                            </TableRow>
                          ))}
                          {/* Column averages */}
                          <TableRow sx={{ bgcolor: 'grey.50' }}>
                            <TableCell />
                            <TableCell sx={{ fontWeight: 700, fontSize: { xs: 10, sm: 12 } }}>Class Average</TableCell>
                            {summary.subjects.map(s => {
                              const vals = summary.learners.map(l => summary.matrix[l.admNo]?.[s.code]?.pct).filter(p => p !== undefined);
                              const avg = vals.length ? vals.reduce((a, b) => a + b, 0) / vals.length : null;
                              return (
                                <TableCell key={s.code} align="center">
                                  {avg !== null ? <Typography variant="caption" fontWeight={700}>{avg.toFixed(1)}%</Typography> : <Typography variant="caption">—</Typography>}
                                </TableCell>
                              );
                            })}
                            <TableCell /><TableCell />
                          </TableRow>
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>
                ) : sumYear && sumTerm && sumStage ? (
                  <Alert severity="info">No scores found for this selection.</Alert>
                ) : (
                  <Typography variant="body2" color="text.secondary">Select Year, Term and Stage to view the class summary.</Typography>
                )}

                {/* Grade legend */}
                <Box mt={2} display="flex" gap={{ xs: 1, sm: 2 }} flexWrap="wrap" alignItems="center" sx={{ px: { xs: 1, sm: 0 } }}>
                  {Object.entries(GRADE_COLORS).filter(([k]) => k).map(([grade, gc]) => (
                    <Box key={grade} display="flex" alignItems="center" gap={0.5}>
                      <Box sx={{ width: 14, height: 14, bgcolor: gc.bg, border: `1px solid ${gc.border}`, borderRadius: 0.5 }} />
                      <Typography variant="caption" sx={{ color: gc.text, fontWeight: 700, fontSize: { xs: 10, sm: 12 } }}>{grade}</Typography>
                    </Box>
                  ))}
                  {rankedLearners.length > 0 && (
                    <Typography variant="caption" color="text.secondary" sx={{ ml: { xs: 0, sm: 2 }, fontSize: { xs: 9, sm: 11 } }}>
                      {rankedLearners.length} learner{rankedLearners.length !== 1 ? 's' : ''} · {summary.subjects.length} subject{summary.subjects.length !== 1 ? 's' : ''}
                    </Typography>
                  )}
                </Box>
              </Box>
            )}

            {/* ── Tab 3: Print / Report Card ── */}
            {tab === 2 && (
              <Box sx={{ px: { xs: 1, sm: 0 } }}>
                <Alert severity="info" sx={{ mb: 2 }}>
                  This tab previews the printable report card. Use <strong>Learner Report</strong> tab to select a learner first, then come here to print.
                </Alert>

                {selLearner && learnerReport.length > 0 ? (
                  <>
                    <Box mb={2}>
                      <TextField
                        label="Teacher Remarks (printed on report)"
                        value={remarks} onChange={e => setRemarks(e.target.value)}
                        fullWidth multiline rows={2} size="small"
                        placeholder="e.g. A hardworking learner who shows great potential…"
                      />
                    </Box>

                    <Paper variant="outlined" sx={{ p: 3, mb: 3, maxWidth: 700, mx: 'auto' }}>
                      <Typography variant="h6" fontWeight={700} align="center" gutterBottom>LEARNER TERM REPORT</Typography>
                      <Typography variant="body2" align="center" color="text.secondary" gutterBottom>Academic Year: {repYear} · Term {repTerm}</Typography>
                      <Divider sx={{ my: 1 }} />
                      <Grid container spacing={1} mb={2}>
                        <Grid item xs={6}><Typography variant="body2"><strong>Name:</strong> {selLearner.name}</Typography></Grid>
                        <Grid item xs={6}><Typography variant="body2"><strong>Adm No:</strong> {selLearner.admNo}</Typography></Grid>
                        <Grid item xs={6}><Typography variant="body2"><strong>Stage:</strong> {selLearner.stage}</Typography></Grid>
                        <Grid item xs={6}><Typography variant="body2"><strong>Class:</strong> {selLearner.classCode || '—'}</Typography></Grid>
                      </Grid>
                      <Table size="small">
                        <TableHead>
                          <TableRow sx={{ bgcolor: 'grey.50' }}>
                            <TableCell><strong>Learning Area</strong></TableCell>
                            <TableCell align="center"><strong>Score</strong></TableCell>
                            <TableCell align="center"><strong>Out Of</strong></TableCell>
                            <TableCell align="center"><strong>%</strong></TableCell>
                            <TableCell align="center"><strong>Grade</strong></TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {learnerReport.map(r => (
                            <TableRow key={r.learningAreaCode}>
                              <TableCell>{r.learningAreaName || r.learningAreaCode}</TableCell>
                              <TableCell align="center">{r.totalRaw ?? '—'}</TableCell>
                              <TableCell align="center">{r.totalMax}</TableCell>
                              <TableCell align="center">{r.percentage?.toFixed(1)}%</TableCell>
                              <TableCell align="center"><GradeCell grade={gradeForRow(r)} /></TableCell>
                            </TableRow>
                          ))}
                          <TableRow sx={{ bgcolor: 'grey.100' }}>
                            <TableCell><strong>TOTAL / MEAN</strong></TableCell>
                            <TableCell align="center"><strong>{totalRawSum}</strong></TableCell>
                            <TableCell align="center"><strong>{totalMaxSum}</strong></TableCell>
                            <TableCell align="center"><strong>{overallPct?.toFixed(1)}%</strong></TableCell>
                            <TableCell align="center"><GradeCell grade={overallGrade} /></TableCell>
                          </TableRow>
                        </TableBody>
                      </Table>
                      {remarks && (
                        <Box mt={2} p={1.5} bgcolor="grey.50" borderRadius={1}>
                          <Typography variant="caption" fontWeight={700}>Teacher Remarks: </Typography>
                          <Typography variant="caption">{remarks}</Typography>
                        </Box>
                      )}
                      <Box mt={3} display="flex" justifyContent="space-between">
                        <Typography variant="caption">Class Teacher: _______________________</Typography>
                        <Typography variant="caption">Date: _______________</Typography>
                      </Box>
                    </Paper>

                    <Box display="flex" justifyContent="center">
                      <Button variant="contained" startIcon={<Print />} onClick={handleLearnerPrint}
                        sx={{ bgcolor: '#004d40', '&:hover': { bgcolor: '#00251a' } }}>
                        Print Report Card
                      </Button>
                    </Box>
                  </>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    Go to the <strong>Learner Report</strong> tab, select a learner, year and term, then come back here to print.
                  </Typography>
                )}
              </Box>
            )}
          </CardContent>
        </Card>
      </Box>
    </>
  );
};

export default AssessmentReportPage;
